/**
 * useIsPhone.js — "is this a phone?", answered by the hardware.
 * ─────────────────────────────────────────────────────────────
 * This was `matchMedia('(max-width: 768px)')` for a long time, and a width is
 * a proxy the user can falsify just by turning the device sideways: an iPhone
 * in landscape is ~852 CSS px, so mid-session the app decided it had become a
 * desktop, reshuffled the layout, and took the phone-only Trade panel out of
 * scope on the way out. Rotating a phone does not turn it into a laptop.
 *
 * So the question moved from the window to the device. Nothing below reads
 * innerWidth; nothing below changes when you rotate. The answer is computed
 * once, at module load, and is fixed for the life of the tab — which is the
 * whole point, and also why `useIsPhone()` holds no state and never triggers
 * a re-render.
 *
 * WHAT DOESN'T BELONG HERE: layout questions that are honestly about available
 * ROOM. Workspace's PORTRAIT_MAX (1081) and the chart's isLandscape (>1100)
 * ask "is there space for two columns", not "is this a phone", and they
 * correctly still key on width. Leave those alone.
 */

/**
 * Manual override, for the day the hardware lies. From the browser console:
 *   localStorage['solcheddar:forcePhone'] = 'on'    // force the phone layout
 *   localStorage['solcheddar:forcePhone'] = 'off'   // force the desktop one
 *   localStorage.removeItem('solcheddar:forcePhone')// back to detection
 * Reload to apply. Without this, a misfire on a device I can't reproduce is
 * unfixable without a rebuild + redeploy, which is a bad place to be.
 */
export const FORCE_KEY = 'solcheddar:forcePhone'

/**
 * Tablets are touch-primary too, so the pointer/hover test alone would hand an
 * iPad in landscape the stacked phone layout — a worse regression than the one
 * being fixed. Gate on the panel's SHORT side in PHYSICAL pixels: 1600 clears
 * every phone on the market (Galaxy S24 Ultra tops out at 1440) and sits under
 * every full-size tablet (iPad 10.9 = 1640, iPad Pro 11 = 1668). iPad mini
 * (1488) lands on the phone side — a deliberate coin-flip, since it's closer
 * to a big phone than to a desktop.
 */
export const PHONE_MAX_SHORT_PX = 1600

/**
 * The actual test. `env` defaults to `window` and exists only so tests can
 * hand it a fake device — every field read below is a real property of window.
 */
export function detectPhone(env = (typeof window !== 'undefined' ? window : null)) {
  if (!env) return false   // SSR / no DOM

  try {
    const forced = env.localStorage?.getItem(FORCE_KEY)
    if (forced === 'on')  return true
    if (forced === 'off') return false
  } catch {}   // Safari private mode throws on localStorage access

  // Chromium answers outright — but only the YES is worth anything. Under
  // "Request desktop site" Chrome reports mobile:false on a real phone, so a
  // NO here means "don't know", not "not a phone". Fall through to hardware
  // rather than `??`-ing a false into the result.
  if (env.navigator?.userAgentData?.mobile === true) return true

  if (typeof env.matchMedia !== 'function') return false

  // Touch-primary: the pointer is a finger, and there is nothing to hover
  // with. True on phones and tablets, false on desktops — INCLUDING
  // touchscreen laptops, whose *primary* pointer is still the trackpad. This
  // is the same signal the chart's y-axis magnifier already trusts (see
  // COARSE_POINTER in FootprintChartCanvas), and it's the one thing that
  // survives desktop-site mode, which is exactly where UA sniffing gives up.
  const coarse = env.matchMedia('(pointer: coarse)').matches
  const noHover = env.matchMedia('(hover: none)').matches
  if (!(coarse && noHover)) return false

  // Phone, not tablet — and PHYSICAL pixels, not CSS pixels. Desktop-site mode
  // rewrites screen.width (a phone starts claiming ~980) but compensates in
  // devicePixelRatio, so the PRODUCT — the actual glass — holds still. And
  // min() of the two sides is invariant under rotation, which is the whole
  // reason we're here: iOS keeps screen.width at the portrait value, Android
  // swaps the pair, min() reads the same either way.
  const dpr = env.devicePixelRatio || 1
  const w = env.screen?.width || 0
  const h = env.screen?.height || 0
  const shortSide = Math.min(w, h) * dpr

  // No screen metrics at all (a locked-down embed) — we already know the
  // pointer is coarse and can't hover, so phone is the better guess.
  return shortSide > 0 ? shortSide <= PHONE_MAX_SHORT_PX : true
}

/** Resolved once. See the header: this is deliberately not live. */
const PHONE = detectPhone()

/** Is this device a phone? Constant for the life of the tab. */
export function isPhoneDevice() { return PHONE }

/**
 * Back-compat alias. The name is now a small lie — nothing about a viewport is
 * consulted — but it's the import WalletHoldings already uses, and renaming it
 * buys nothing. New code should call isPhoneDevice().
 */
export const isPhoneViewport = isPhoneDevice

/**
 * React-facing form, so call sites keep reading like hooks. No state, no
 * listener, no re-render: the device does not change underneath a mounted
 * component. Components that were subscribing to a matchMedia change event
 * were subscribing to the bug.
 */
export function useIsPhone() { return PHONE }

/**
 * Style hook for the CSS. `<html class="is-phone">` replaces the old
 * `@media (max-width: 768px)` wrapper in styles.css, so the stylesheet asks
 * the same question the JS does instead of a parallel, differently-wrong one.
 *
 * Applied at module evaluation — before React has rendered anything — so there
 * is no flash of desktop layout to reflow out of.
 */
if (typeof document !== 'undefined') {
  document.documentElement.classList.toggle('is-phone', PHONE)
}

/**
 * Debug probe. Phones are the one class of device I can't put a devtools
 * console on easily, so when detection misfires in the wild this is what makes
 * it diagnosable: `__cheddarPhone` in the address bar of a desktop-paired
 * session, or read it out of a screenshot.
 */
if (typeof window !== 'undefined') {
  window.__cheddarPhone = () => {
    const mm = (q) => { try { return window.matchMedia(q).matches } catch { return null } }
    let forced = null
    try { forced = window.localStorage?.getItem(FORCE_KEY) } catch {}
    return {
      phone: PHONE,
      forced,
      uaDataMobile: navigator.userAgentData?.mobile ?? null,
      coarse: mm('(pointer: coarse)'),
      noHover: mm('(hover: none)'),
      dpr: window.devicePixelRatio || 1,
      screen: [screen.width, screen.height],
      shortSidePhysical: Math.min(screen.width || 0, screen.height || 0) * (window.devicePixelRatio || 1),
      threshold: PHONE_MAX_SHORT_PX,
      // For comparison only — this is the signal we STOPPED trusting.
      oldWidthTrigger: mm('(max-width: 768px)'),
      innerWidth: window.innerWidth,
      htmlHasClass: document.documentElement.classList.contains('is-phone'),
    }
  }
}

export default useIsPhone
